import type { ChartData, ChartQuerySpec, KpiTile, ValueFormat } from '../types';
import { ICON_NAMES, TONES } from '../charts/icons';
import { humanise, isNumeric } from '../charts/format';

/**
 * Authoring a KPI row's tiles.
 *
 * The design constraint that shaped this: **every field is optional**. A tile
 * with nothing but a column chosen already draws a finished-looking card,
 * because the icon, the colour and the number format are all inferred. So the
 * editor leads with "Add a tile for every number" — one click that fills the
 * row from the result — and the per-tile controls are there for the cases where
 * a default is wrong, not as a form to complete.
 *
 * That is the difference from the hand-written fragment this replaces: the
 * fragment had no settings at all, so changing anything meant editing HTML.
 */

interface Props {
  tiles: KpiTile[];
  onChange: (tiles: KpiTile[]) => void;
  /** The previewed result — the source of column names for a single-query chart. */
  data: ChartData | null;
  /** A composite chart's named queries, when it has them. */
  queries?: ChartQuerySpec[];
}

const AGGS: Array<{ value: NonNullable<KpiTile['agg']>; label: string }> = [
  { value: 'latest', label: 'Latest row' },
  { value: 'first', label: 'First row' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'max', label: 'Highest' },
  { value: 'min', label: 'Lowest' },
  { value: 'count', label: 'Count of distinct values' },
];

const FORMATS: Array<{ value: ValueFormat['kind']; label: string }> = [
  { value: 'number', label: 'Number (12.9K)' },
  { value: 'currency', label: 'Currency ($12.9K)' },
  { value: 'percent', label: 'Percent (87%)' },
  { value: 'rating', label: 'Rating (filled stars)' },
  { value: 'rank', label: 'Rank (#1)' },
  { value: 'duration', label: 'Duration (1m 30s)' },
  { value: 'bytes', label: 'Size (4.2MB)' },
  { value: 'text', label: 'Text, as it came' },
];

/** The column names worth offering as a measure, numeric ones first. */
const columnsOf = (data: ChartData | null): string[] => {
  if (!data?.columns.length) return [];
  const numericFirst = data.columns.map((name, i) => ({
    name,
    numeric: data.rows.slice(0, 20).some((row) => isNumeric(row[i])),
  }));
  return [
    ...numericFirst.filter((c) => c.numeric).map((c) => c.name),
    ...numericFirst.filter((c) => !c.numeric).map((c) => c.name),
  ];
};

export const TileEditor = ({ tiles, onChange, data, queries }: Props) => {
  const columns = columnsOf(data);

  const update = (index: number, patch: Partial<KpiTile>) =>
    onChange(tiles.map((tile, i) => (i === index ? { ...tile, ...patch } : tile)));

  const remove = (index: number) => onChange(tiles.filter((_, i) => i !== index));

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= tiles.length) return;
    const next = [...tiles];
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
  };

  /**
   * One click for the common case: a tile per numeric column, capped at six
   * because that is where the grid stops being a row. Everything else about
   * each tile is inferred, so this is usually the only action needed.
   */
  const fillFromResult = () => {
    if (!data) return;
    const numeric = data.columns.filter((_, i) => data.rows.slice(0, 20).some((row) => isNumeric(row[i])));
    const made = (numeric.length ? numeric : data.columns).slice(0, 6).map((measure) => ({ measure }));
    onChange(made);
  };

  return (
    <div className="tiles">
      <h4 className="tiles__head">Tiles</h4>
      <p className="hint">
        One tile per number. Name a column and the rest is worked out — the label from the column
        name, the icon and colour from what it measures, the format from the kind of number it is.
        Change any of that below only where the guess is wrong. With no tiles at all the row infers
        them from the result, so it is never blank.
      </p>

      <div className="tiles__actions">
        <button type="button" className="chip" onClick={() => onChange([...tiles, {}])}>
          + Add a tile
        </button>
        {data && data.columns.length > 0 && (
          <button type="button" className="chip" onClick={fillFromResult}>
            + Add a tile for every number
          </button>
        )}
        {tiles.length > 0 && (
          <button type="button" className="chip" onClick={() => onChange([])}>
            Clear — go back to inferred
          </button>
        )}
      </div>

      {tiles.map((tile, index) => (
        <details key={index} className="tile-row" open={tiles.length <= 3}>
          <summary>
            <span className="tile-row__n">{index + 1}</span>
            <span className="tile-row__name">
              {tile.label?.trim() || (tile.measure ? humanise(tile.measure) : 'Row count')}
            </span>
            <span className="tile-row__src">{tile.query ? `${tile.query}.` : ''}{tile.measure ?? '—'}</span>
          </summary>

          <div className="tile-row__grid">
            {queries && queries.length > 0 && (
              <label>
                <span>Source</span>
                <select
                  value={tile.query ?? ''}
                  onChange={(e) => update(index, { query: e.target.value || undefined })}
                >
                  <option value="">The chart's own query</option>
                  {queries.map((q) => (
                    <option key={q.key} value={q.key}>
                      {q.key} — chart {q.chartId}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              <span>Column</span>
              <select
                value={tile.measure ?? ''}
                onChange={(e) => update(index, { measure: e.target.value || undefined })}
              >
                {/* No column is a real choice: it counts the rows, which is the
                    only honest source for a figure like "Countries" when the
                    query returns one row per country and no total. */}
                <option value="">Count the rows</option>
                {columns.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Figure</span>
              <select
                value={tile.agg ?? ''}
                onChange={(e) =>
                  update(index, { agg: (e.target.value || undefined) as KpiTile['agg'] })
                }
              >
                <option value="">Latest row (default)</option>
                {AGGS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Label</span>
              <input
                value={tile.label ?? ''}
                onChange={(e) => update(index, { label: e.target.value || undefined })}
                placeholder={tile.measure ? humanise(tile.measure) : 'Rows'}
              />
            </label>

            <label>
              <span>Format</span>
              <select
                value={tile.format?.kind ?? ''}
                onChange={(e) => {
                  const kind = e.target.value as ValueFormat['kind'] | '';
                  if (!kind) return update(index, { format: undefined });
                  // A rating needs its ceiling, and 5 is what every app store
                  // uses; a currency needs a code to pick a symbol.
                  const format =
                    kind === 'rating'
                      ? ({ kind, max: 5 } as ValueFormat)
                      : kind === 'currency'
                        ? ({ kind, code: 'USD', compact: true } as ValueFormat)
                        : ({ kind } as ValueFormat);
                  update(index, { format });
                }}
              >
                <option value="">Auto</option>
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </label>

            {tile.format?.kind === 'percent' && (
              <label>
                <span>Column holds</span>
                <select
                  value={tile.format.scale ?? ''}
                  onChange={(e) => {
                    const scale = e.target.value as 'fraction' | 'percent' | '';
                    update(index, {
                      format: { kind: 'percent', ...(scale ? { scale } : {}) },
                    });
                  }}
                >
                  {/* Auto guesses from magnitude and is wrong above 100%: a
                      genuine 250% arriving as 2.5 reads as "2.5%". Worth one
                      control on the tile that shows a change figure. */}
                  <option value="">Auto — guess from the value</option>
                  <option value="fraction">A fraction (0.87 = 87%)</option>
                  <option value="percent">A percentage (87 = 87%)</option>
                </select>
              </label>
            )}

            <label>
              <span>Icon</span>
              <select
                value={tile.icon ?? ''}
                onChange={(e) => update(index, { icon: e.target.value || undefined })}
              >
                <option value="">Auto</option>
                <option value="none">None</option>
                {ICON_NAMES.map((n) => (
                  <option key={n} value={n}>{humanise(n)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Colour</span>
              <select
                value={tile.tone ?? ''}
                onChange={(e) => update(index, { tone: e.target.value || undefined })}
              >
                <option value="">Auto</option>
                {TONES.map((t) => (
                  <option key={t} value={t}>{humanise(t)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Caption</span>
              <input
                value={tile.caption ?? ''}
                onChange={(e) => update(index, { caption: e.target.value || undefined })}
                placeholder="vs last month"
              />
            </label>

            <label>
              <span>Compare with</span>
              <select
                value={tile.compareTo ?? ''}
                onChange={(e) => update(index, { compareTo: e.target.value || undefined })}
              >
                {/* Without a column, "Latest row" compares against the row
                    before it — which is what "vs last month" means on a
                    monthly series. Anything else has no comparison and draws
                    no arrow, rather than a 0% that claims no change. */}
                <option value="">The previous row</option>
                {columns.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <label className="tile-row__wide">
              <span>Tooltip (i)</span>
              <input
                value={tile.description ?? ''}
                onChange={(e) => update(index, { description: e.target.value || undefined })}
                placeholder="Estimated monthly downloads. The arrow compares it with the month before."
              />
            </label>
          </div>

          <div className="tile-row__tools">
            <button type="button" className="ghost" onClick={() => move(index, -1)} disabled={index === 0}>
              Move up
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => move(index, 1)}
              disabled={index === tiles.length - 1}
            >
              Move down
            </button>
            <button type="button" className="ghost" onClick={() => remove(index)}>
              Remove
            </button>
          </div>
        </details>
      ))}
    </div>
  );
};
